import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Search, Users, Car, CalendarDays, FileText, Receipt, Loader2, Package, X, ChevronRight } from "lucide-react";

const GROUPS = [
  { key: "clients", label: "Clients", icon: Users, to: (r) => `/clients/${r.id}`, action: "Ouvrir le client" },
  { key: "vehicles", label: "Véhicules", icon: Car, to: (r) => `/vehicles/${r.id}`, action: "Ouvrir le véhicule" },
  { key: "appointments", label: "Rendez-vous", icon: CalendarDays, to: (r) => `/calendar?appt=${r.id}`, action: "Ouvrir le rendez-vous" },
  { key: "quotes", label: "Devis", icon: FileText, to: (r) => `/quotes?open=${r.id}`, action: "Ouvrir le devis" },
  { key: "invoices", label: "Factures", icon: Receipt, to: (r) => `/invoices?focus=${r.id}`, action: "Ouvrir la facture" },
  { key: "products", label: "Produits", icon: Package, to: () => `/stock`, action: "Ouvrir le stock" },
];

function useSearch() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (q.trim().length < 2) { setRes(null); setLoading(false); return undefined; }
    setLoading(true);
    let live = true;
    const t = setTimeout(async () => {
      try { const { data } = await api.get("/search", { params: { q: q.trim() } }); if (live) setRes(data); }
      catch { /* ignore */ } finally { if (live) setLoading(false); }
    }, 300);
    return () => { live = false; clearTimeout(t); };
  }, [q]);
  return { q, setQ, res, setRes, loading };
}

function Results({ res, onPick }) {
  const total = GROUPS.reduce((n, g) => n + (res[g.key]?.length || 0), 0);
  if (!total) return <div className="px-3 py-6 text-sm text-slate-500 text-center" data-testid="global-search-empty">Aucun résultat</div>;
  return GROUPS.map((g) => (res[g.key]?.length ? (
    <div key={g.key} className="mb-1" data-testid={`search-group-${g.key}`}>
      <div className="px-2 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5"><g.icon className="w-3 h-3" />{g.label}</div>
      {res[g.key].map((r) => (
        <button key={r.id} data-testid={`search-result-${g.key}-${r.id}`} onClick={() => onPick(g.to(r))} title={g.action}
          className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-slate-100 active:bg-slate-100 flex items-center gap-2">
          <span className="min-w-0 flex-1">
            <span className="block text-sm text-slate-800 font-medium truncate">{r.label}</span>
            {r.sub && <span className="block text-xs text-slate-500 truncate">{r.sub}</span>}
          </span>
          <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
        </button>
      ))}
    </div>
  ) : null));
}

const PLACEHOLDER = "Rechercher dans Auto Detail Manager…";

export default function GlobalSearch({ compact = false }) {
  const { q, setQ, res, setRes, loading } = useSearch();
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (compact) return undefined;
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [compact]);

  const go = (to) => { setOpen(false); setQ(""); setRes(null); navigate(to); };
  const input = (
    <input data-testid="global-search-input" value={q} autoFocus={compact} type="text"
      onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)}
      onKeyDown={(e) => { if (e.key === "Escape") setOpen(false); }}
      placeholder={PLACEHOLDER} className="bg-transparent text-sm outline-none w-full text-slate-700 placeholder:text-slate-400" />
  );

  if (compact) {
    return (
      <>
        <button onClick={() => setOpen(true)} data-testid="global-search-open" aria-label="Rechercher" className="p-2 rounded-lg text-slate-700 hover:bg-slate-100"><Search className="w-5 h-5" /></button>
        {open && (
          <div className="fixed inset-0 z-[60] bg-white flex flex-col" data-testid="global-search">
            <div className="flex items-center gap-2 px-3 h-14 border-b border-slate-200">
              <Search className="w-4 h-4 text-slate-400 shrink-0" />
              {input}
              {loading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
              <button onClick={() => { setOpen(false); setQ(""); }} data-testid="global-search-close" aria-label="Fermer" className="p-2 text-slate-500"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-2" data-testid="global-search-results">
              {res ? <Results res={res} onPick={go} /> : <div className="px-3 py-6 text-sm text-slate-400 text-center">Tapez au moins 2 caractères</div>}
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <div className="relative" ref={boxRef} data-testid="global-search">
      <div className="flex items-center gap-2 bg-slate-100 rounded-lg px-3 h-9 border border-transparent focus-within:border-emerald-400 transition">
        <Search className="w-4 h-4 text-slate-400 shrink-0" />
        {input}
        {loading && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
      </div>
      {open && res && (
        <div data-testid="global-search-results" className="absolute z-50 mt-2 w-[26rem] max-h-[70vh] overflow-y-auto right-0 bg-white border border-slate-200 rounded-xl shadow-xl p-2">
          <Results res={res} onPick={go} />
        </div>
      )}
    </div>
  );
}
